const FormProgressUtils = require('hooks/useFormPrimitives/progress');

const SignupProgressHelpers = module.exports = {};

const internals = {};

internals.DEFAULT_SIGNUP_PROGRESS_DEBOUNCE_MS = 700;
internals.progressSaveStates = new WeakMap();

internals.shouldRunSignupProgress = ({
    formProps = {}
} = {}) => formProps.mode === 'signup' && typeof formProps.saveProfileProgress === 'function';

internals.getProgressSaveState = ({
    formProps = {}
}) => {

    if (typeof formProps.saveProfileProgress !== 'function') {
        return null;
    }

    if (!internals.progressSaveStates.has(formProps.saveProfileProgress)) {
        internals.progressSaveStates.set(formProps.saveProfileProgress, {
            payload: {},
            timer: null
        });
    }

    return internals.progressSaveStates.get(formProps.saveProfileProgress);
};

internals.getProgressDebounceMs = ({
    formProps = {}
}) => Number.isFinite(formProps.signupProgressDebounceMs)
    ? formProps.signupProgressDebounceMs
    : internals.DEFAULT_SIGNUP_PROGRESS_DEBOUNCE_MS;

internals.scheduleSignupProgressSave = ({
    formProps = {},
    payload = {}
}) => {

    const saveState = internals.getProgressSaveState({
        formProps
    });

    if (!saveState) {
        return;
    }

    saveState.payload = {
        ...saveState.payload,
        ...payload
    };

    if (saveState.timer) {
        clearTimeout(saveState.timer);
    }

    saveState.timer = setTimeout(() => {

        const nextPayload = saveState.payload;

        saveState.payload = {};
        saveState.timer = null;

        if (!Object.keys(nextPayload).length) {
            return;
        }

        formProps.saveProfileProgress(nextPayload);
    }, internals.getProgressDebounceMs({
        formProps
    }));
};

internals.mergeOnChangeEffects = ({
    field = {},
    effect
}) => {
    const existingEffects = field.onChangeEffects;

    if (!existingEffects) {
        return [effect];
    }

    return Array.isArray(existingEffects)
        ? existingEffects.concat(effect)
        : [existingEffects, effect];
};

internals.isEmptyProgressValue = (value) => value === '' || value === null || typeof value === 'undefined';

internals.shouldSkipSignupProgressPayload = ({
    field = {},
    payload = {}
}) => {

    if (!field.required) {
        return false;
    }

    return Object.values(payload).some(internals.isEmptyProgressValue);
};

internals.buildProgressPayload = ({
    field,
    fieldName,
    formMethods,
    value
}) => {

    const payload = typeof field.buildSignupProgressPayload === 'function'
        ? field.buildSignupProgressPayload({
            field,
            fieldName,
            formMethods,
            value
        })
        : {
            [fieldName]: value
        };

    if (internals.shouldSkipSignupProgressPayload({
        field,
        payload
    })) {
        return null;
    }

    return payload;
};

SignupProgressHelpers.buildOnChangeEffect = () => ({
    when: ({
        formProps
    }) => internals.shouldRunSignupProgress({
        formProps
    }),
    sideEffect: ({
        field,
        fieldName,
        formConfig,
        formMethods,
        formProps,
        value
    }) => {

        const payload = internals.buildProgressPayload({
            field,
            fieldName,
            formMethods,
            value
        });

        if (!payload) {
            return;
        }

        internals.scheduleSignupProgressSave({
            formProps,
            payload: FormProgressUtils.buildProgressPayload({
                payload,
                definitions: formConfig.definitions
            })
        });
    }
});

SignupProgressHelpers.withOnChangeProgress = (field = {}) => {

    if (
        field.trackProfileProgress === false
        || field.render === false
        || field.type === 'hidden'
    ) {
        return field;
    }

    return {
        ...field,
        onChangeEffects: internals.mergeOnChangeEffects({
            field,
            effect: SignupProgressHelpers.buildOnChangeEffect()
        })
    };
};

SignupProgressHelpers.internals = internals;
